import { useState, useCallback } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import { Flex, VolumeOffIcon } from '@buffer-mono/popcorn'
import { PostFields } from '@buffer-mono/reminders-config'

import type { Image } from '~publish/legacy/composer/composer/entities/factories'

import { LikeIcon, ShareIcon } from '../../../../assets/instagram'
import {
  EmptyMediaPlaceholder,
  PostWrapper,
  PreviewAiMarker,
} from '../../../Common/styles'
import { DEFAULT_AVATAR, gifAsImage } from '../../../Common/utils'
import { VideoPlayer as VideoPlayerCommon } from '../../../Common/VideoPlayer'
import type { InstagramContent, ProfilePreview } from '../../../types'
import { ImageCarousel } from '../ImageCarousel'
import type { ImageCarouselSlide } from '../ImageCarousel'
import { PreviewAvatar } from '../PreviewAvatar'
import { MusicStickerIcon } from './components/MusicStickerIcon'

import styles from './Story.module.css'
import instagramStyles from '../../InstagramPreview.module.css'

const ImagePreview = ({ image }: { image: Image }): JSX.Element => (
  <div className={styles.imageWrapper} data-testid="preview-images">
    <img
      className={styles.image}
      src={image.url}
      alt={image.altText || 'Post image'}
    />
  </div>
)

const Story = ({
  content,
  profile,
}: {
  content: InstagramContent
  profile: ProfilePreview
}): JSX.Element => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  const { avatar, username } = profile
  const { video, images, gif, isAiGenerated } = content

  // `content.assets` is the ordered image/video/gif source of truth when the
  // caller provides it — a Story carries the same multi-asset attachment as
  // a Post (see instagramRules' shared maxAssetsRule(10)), so it can hold
  // several slides. Callers without it (older fixtures, network content
  // types without an assets field) fall back to the legacy
  // images-array-or-gif-fallback plus a separate video field.
  const slides: ImageCarouselSlide[] = content.assets?.length
    ? content.assets.reduce<ImageCarouselSlide[]>((acc, asset) => {
        if (asset.kind === 'image') {
          acc.push({ kind: 'image', image: asset.image })
        } else if (asset.kind === 'gif') {
          acc.push({ kind: 'image', image: gifAsImage(asset.gif) })
        } else if (asset.kind === 'video') {
          acc.push({ kind: 'video', video: asset.video })
        }
        return acc
      }, [])
    : [
        ...(images?.length
          ? images.map((image) => ({ kind: 'image' as const, image }))
          : gif
            ? [{ kind: 'image' as const, image: gifAsImage(gif) }]
            : []),
        ...(video ? [{ kind: 'video' as const, video }] : []),
      ]

  const totalSlides = slides.length
  const hasSlides = totalSlides > 0
  const hasCarousel = totalSlides > 1
  const singleSlide = totalSlides === 1 ? slides[0] : null
  // The single-video layout absolutely positions the player over the full
  // story frame; every other case (single image, carousel) flows in the
  // wrapper's flex layout instead — see .postWrapperHasImage.
  const isSingleVideo = singleSlide?.kind === 'video'
  const activeSlide = hasCarousel ? slides[currentSlideIndex] : singleSlide
  const isActiveSlideVideo = activeSlide?.kind === 'video'

  const handleSlideIndexChange = useCallback((index: number) => {
    setCurrentSlideIndex(index)
  }, [])

  const stickerLink =
    content.isReminder && content.selectedStickers?.includes(PostFields.LINK)
      ? content?.channelData?.instagram?.link
      : ''
  const stickerMusic =
    content.isReminder && content.selectedStickers?.includes(PostFields.MUSIC)
      ? content?.channelData?.instagram?.music
      : ''
  const stickerText =
    content.isReminder && content.selectedStickers?.includes(PostFields.TEXT)
      ? content?.channelData?.instagram?.text
      : ''

  return (
    <PostWrapper
      className={clsx(
        styles.postWrapper,
        !isSingleVideo && hasSlides && styles.postWrapperHasImage,
      )}
      data-testid="story"
    >
      {stickerText && (
        <div className={instagramStyles.textStickerWrapper}>
          <span className={instagramStyles.textStickerContent} translate="no">
            {stickerText}
          </span>
        </div>
      )}

      {stickerLink && (
        <div className={styles.stickerLinkWrapper}>
          <img
            src="https://buffer-publish.s3.amazonaws.com/images/instagram_link_sticker.png"
            alt="Instagram link sticker preview"
          />
        </div>
      )}

      {isSingleVideo && (
        <VideoPlayerCommon
          video={singleSlide.video}
          className={styles.videoPlayer}
        />
      )}
      {hasCarousel && (
        <ImageCarousel
          slides={slides}
          onIndexChange={handleSlideIndexChange}
          variant="story"
        />
      )}
      {singleSlide?.kind === 'image' && (
        <ImagePreview image={singleSlide.image} />
      )}
      {!hasSlides && (
        <Flex className={styles.emptyWrapper}>
          <EmptyMediaPlaceholder service="instagram" className={styles.empty} />
        </Flex>
      )}
      <div className={styles.header}>
        <div className={styles.barsWrapper}>
          {/* Render one progress bar per slide - minimum 1 bar */}
          {Array.from(
            { length: Math.max(totalSlides, 1) },
            (_, barIndex) => barIndex + 1,
          ).map((barPosition) =>
            barPosition - 1 <= currentSlideIndex ? (
              <div
                key={`story-progress-${barPosition}`}
                data-testid="progress-bar"
                className={styles.bar}
              />
            ) : (
              <div
                key={`story-progress-${barPosition}`}
                data-testid="progress-bar"
                className={styles.barTransparent}
              />
            ),
          )}
        </div>
        <div className={styles.previewHeaderWrapper}>
          <div className={styles.headerDetailsWrapper}>
            <PreviewAvatar
              className={instagramStyles.avatar}
              data-testid="avatar"
              src={avatar}
              fallbackUrl={DEFAULT_AVATAR}
            />
            <div className={styles.detailsWrapper}>
              <div className={styles.usernameTimeWrapper}>
                <span className={styles.username} translate="no">
                  {username}
                </span>
                <div className={styles.time}>21h</div>
              </div>
              {stickerMusic && (
                <span className={styles.music}>
                  <MusicStickerIcon /> Music
                </span>
              )}
              {isAiGenerated && (
                <PreviewAiMarker label="AI info" className={styles.aiMarker} />
              )}
            </div>
            <div className={styles.headerVolumeWrapper}>
              <VolumeOffIcon
                data-testid="volume-off-icon"
                className={clsx(
                  styles.volumeOff,
                  isActiveSlideVideo && styles.volumeOffVisible,
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.field}>Send message</div>
        <LikeIcon size={20} color="white" />
        <ShareIcon size={20} color="white" />
      </div>
    </PostWrapper>
  )
}

export default Story
